import moment from 'moment';
import { TIMERECORDING_TYPE_OF_WORK_CODES } from '../../../models/complex/time-recording-entry.model';
import { TimesheetEntryState, TimesheetState } from '../timesheet-collection.state';

/**
 * Calendar-day key a portion of work is attributed to.
 *
 * Days are local: work is measured against the technician's own midnight, not UTC's, because the
 * panel shows the technician's week.
 */
export const WORKDAY_KEY_FORMAT = 'YYYY-MM-DD';

/**
 * Working minutes split into what is counted as done and what is recorded but not yet confirmed.
 *
 * Kept in minutes rather than hours because entries start and end on whole minutes: summed as
 * minutes they add up exactly, whereas most minute counts have no exact binary representation as a
 * fraction of an hour (11 minutes is 0.18333… hours), and the error accumulates across entries and
 * days until the panel shows a minute the timesheet does not.
 *
 * The split exists because a work entry whose confirmation has not been submitted is still shown to
 * the technician — it occupies time on the timesheet — but it has not been through confirmation, so
 * it must not be folded into the same figure as minutes that have. `committedInMinutes` and
 * `inProgressInMinutes` are always summed back together for the totals every existing consumer reads
 * (`getTotalWeekDuration`, `getCurrentDayTotalDuration`); the split is additive, not a new total.
 */
export interface WorkdayDurations {
    /** Counted as done: submitted confirmations and manual gap entries. */
    committedInMinutes: number;
    /** Recorded, not yet confirmed: work entries whose confirmation is still open. */
    inProgressInMinutes: number;
}

/**
 * A single day of the running work week, as shown in the weekly work time panel.
 *
 * Derived from the timesheet collection, so a day carries the two durations of
 * {@link WorkdayDurations}: the open and the submitted timesheets of that day are simply summed.
 * Nothing is persisted, so there is no separately stored per-day total that could drift from the
 * timesheets it was computed from.
 */
export interface WeeklyWorkdayModel {
    /** start of the calendar day */
    workDate: moment.Moment;
    committedInMinutes: number;
    inProgressInMinutes: number;
}

/** Decides whether a manual entry's task type is one of the configured pause codes. */
export type PauseCodePredicate = (taskType: string) => boolean;

/** A day nothing has been booked on yet. */
const noDurations = (): WorkdayDurations => ({ committedInMinutes: 0, inProgressInMinutes: 0 });

/**
 * An entry's working minutes, split across the local calendar days it actually covers.
 *
 * An entry is not confined to the day its timesheet began on: a timesheet may hold entries from
 * several days, and a single entry may run past midnight. Such an entry is cut at every local
 * midnight it crosses and each piece is attributed to the day that piece falls in, so a 22:00–02:00
 * shift contributes two hours to each of the two days rather than four to the first one.
 *
 * @param start ISO timestamp the entry starts at
 * @param end ISO timestamp the entry ends at
 * @returns minutes per {@link WORKDAY_KEY_FORMAT} day key
 */
function entryMinutesByDate(start: string, end: string): Record<string, number> {
    const startTime = moment(start);
    const endTime = moment(end);

    // An unset or unparsable end is one of the empty strings the seeded and hydrated states carry,
    // and an entry ending before it starts is not a negative amount of work. Neither covers any day.
    if (!startTime.isValid() || !endTime.isValid() || !endTime.isAfter(startTime)) {
        return {};
    }

    const minutesByDate: Record<string, number> = {};
    let segmentStart = startTime;

    while (segmentStart.isBefore(endTime)) {
        // Whole calendar days rather than 24-hour offsets, so a day that a daylight-saving change
        // makes 23 or 25 hours long is still cut at its own midnight.
        const nextMidnight = segmentStart.clone().add(1, 'day').startOf('day');
        const segmentEnd = moment.min(nextMidnight, endTime);
        const dayKey = segmentStart.format(WORKDAY_KEY_FORMAT);

        minutesByDate[dayKey] =
            (minutesByDate[dayKey] ?? 0) + moment.duration(segmentEnd.diff(segmentStart)).asMinutes();
        segmentStart = segmentEnd;
    }
    return minutesByDate;
}

/**
 * Which of the two figures an entry counts towards:
 *
 * | Entry                                                       | Goes to         |
 * | ------------------------------------------------------------ | --------------- |
 * | work entry, confirmation submitted, not `BREAK`               | committed       |
 * | work entry, confirmation **not** submitted, not `BREAK`       | in progress     |
 * | manual entry, task type set and not a configured pause code   | committed       |
 * | `BREAK` work entries                                          | neither         |
 * | manual entry whose task type is a configured pause code       | neither         |
 * | manual entry with no task type selected                       | neither         |
 *
 * Manual gap entries count as committed because they are typed straight into the timesheet — there
 * is no confirmation behind them, so "not yet submitted" is not a state they can be in.
 *
 * @param entry the entry to classify
 * @param isPauseCode decides whether a manual entry's task type is a configured pause code
 * @returns the field of {@link WorkdayDurations} to add the entry to, or undefined for neither
 */
function entryBucket(
    entry: Readonly<TimesheetEntryState>,
    isPauseCode: PauseCodePredicate,
): keyof WorkdayDurations | undefined {
    if (entry.kind === 'work') {
        if (entry.workCode === TIMERECORDING_TYPE_OF_WORK_CODES.BREAK) {
            return undefined;
        }
        return entry.isConfirmationSubmitted ? 'committedInMinutes' : 'inProgressInMinutes';
    }
    // Without a selected task type there is nothing to test against the pause codes, so the entry
    // cannot be classified and is left out.
    return entry.taskType.length > 0 && !isPauseCode(entry.taskType) ? 'committedInMinutes' : undefined;
}

/**
 * Working minutes a single timesheet contributes, per calendar day and split into committed and
 * in-progress. Each entry is attributed to the days it covers rather than to the timesheet's start
 * of work — see {@link entryMinutesByDate} — so a timesheet spanning several days is spread across
 * them.
 *
 * @param timesheet the timesheet to measure
 * @param isPauseCode decides whether a manual entry's task type is a configured pause code
 * @returns committed and in-progress working minutes per {@link WORKDAY_KEY_FORMAT} day key
 */
export function timesheetWorkDurationsByDate(
    timesheet: Readonly<TimesheetState>,
    isPauseCode: PauseCodePredicate,
): Record<string, WorkdayDurations> {
    const durations: Record<string, WorkdayDurations> = {};

    for (const entry of timesheet.entries ?? []) {
        const bucket = entryBucket(entry, isPauseCode);

        if (bucket === undefined) {
            continue;
        }

        for (const [dayKey, minutes] of Object.entries(entryMinutesByDate(entry.start, entry.end))) {
            const day = durations[dayKey] ?? noDurations();
            durations[dayKey] = { ...day, [bucket]: day[bucket] + minutes };
        }
    }
    return durations;
}

/**
 * Groups the working minutes of the whole collection by calendar day, summing everything that lands
 * on the same day — a technician may submit more than once a day, and two timesheets may each
 * cover part of it.
 *
 * The timesheet's own `startOfWork` is not consulted: entries carry their own timestamps, and those
 * decide the day. A timesheet with no entries therefore contributes nothing, which is what the
 * seeded placeholders the slice starts out with are.
 *
 * @param timesheets the whole timesheet collection, open and submitted alike
 * @param isPauseCode decides whether a manual entry's task type is a configured pause code
 * @returns committed and in-progress working minutes per {@link WORKDAY_KEY_FORMAT} day key
 */
export function workDurationsByDate(
    timesheets: readonly TimesheetState[],
    isPauseCode: PauseCodePredicate,
): Record<string, WorkdayDurations> {
    const durations: Record<string, WorkdayDurations> = {};

    for (const timesheet of timesheets) {
        for (const [dayKey, contribution] of Object.entries(timesheetWorkDurationsByDate(timesheet, isPauseCode))) {
            const day = durations[dayKey] ?? noDurations();
            durations[dayKey] = {
                committedInMinutes: day.committedInMinutes + contribution.committedInMinutes,
                inProgressInMinutes: day.inProgressInMinutes + contribution.inProgressInMinutes,
            };
        }
    }
    return durations;
}

/**
 * The days of the running ISO week up to and including `now`, Monday first, zero-filled where
 * no timesheet exists — the derived equivalent of `createWeeklyWorkTimeList`.
 *
 * Deriving the window instead of storing it removes the two rules the persisted list needed:
 * days of an earlier week are simply not projected (the "is this a new week" reset), and a day
 * the user never opened the app on projects as zero (the backfill loop).
 *
 * @param durationsByDate committed and in-progress working minutes per day, see {@link workDurationsByDate}
 * @param now the moment the week is projected around
 * @returns one entry per day from Monday to `now`
 */
export function buildWeeklyWorkdays(
    durationsByDate: Readonly<Record<string, WorkdayDurations>>,
    now: moment.Moment,
): WeeklyWorkdayModel[] {
    // As per ISO 8601 (https://en.wikipedia.org/wiki/ISO_8601#Week_dates) Monday is day 1 and
    // Sunday day 7, so the weekday number is also the number of days to project.
    return Array.from({ length: now.isoWeekday() }, (_unused, dayOffset) => {
        const workDate = now.clone().startOf('isoWeek').add(dayOffset, 'day');
        return { workDate, ...(durationsByDate[workDate.format(WORKDAY_KEY_FORMAT)] ?? noDurations()) };
    });
}
